/**
 * Shop store — manages shops and sections for the current user.
 */
import { create } from 'zustand';
import { getAuthHeaders } from './authStore';
import { apiUrl } from '../config';
import type { Dataset, DatasetsManifest } from '../types/dataset';

/** Standalone enriched shops selector (avoids circular reference in store) */
export function getEnrichedShops(): EnrichedPublicShop[] {
  const { publicShops, publicDatasets } = useShopStore.getState();
  const datasetByShopId = new Map<string, Dataset>();
  for (const ds of publicDatasets) {
    const sid = ds.shopId || ds.id;
    const existing = datasetByShopId.get(sid);
    if (!existing || (ds.camera_count > existing.camera_count)) {
      datasetByShopId.set(sid, ds);
    }
  }
  return publicShops.map((shop): EnrichedPublicShop => {
    const dataset = datasetByShopId.get(shop.id);
    return {
      ...shop,
      dataset,
      cameraCount: dataset?.camera_count,
      has3DViewer: !!dataset,
    };
  });
}

export interface Section {
  sectionId: string;
  name: string;
  order: number;
  posterFrameIndex?: number;
}

export interface ScanRecord {
  id: string;
  shopId: string;
  sectionId: string;
  sectionName: string;
  version: number;
  status: string;         // uploading | queued | processing | ready | failed
  frameCount: number;
  framesUploaded: number;
  startedAt: string;
  completedAt: string | null;
  resultBlobPath: string | null;
  processingTime?: string | null;
  error?: string | null;
  datasetId?: string | null;
}

export interface Shop {
  id: string;
  userId: string;
  name: string;
  address: string;
  location: { lat: number; lng: number } | null;
  category: string;
  visibility: string;     // "private" | "public"
  description?: string;
  phone?: string;
  whatsapp?: string;
  openingHours?: string;
  posterSectionId?: string;
  sections: Section[];
  pendingTransfer?: {
    token: string;
    createdAt: string;
    expiresAt: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

/** Lightweight public shop (no userId, no pendingTransfer) */
export type PublicShop = Omit<Shop, 'userId' | 'pendingTransfer'>;

/** Public shop enriched with dataset info for the unified browse page */
export interface EnrichedPublicShop extends PublicShop {
  dataset?: Dataset;
  cameraCount?: number;
  has3DViewer: boolean;
}

interface ShopState {
  shops: Shop[];
  loading: boolean;
  error: string | null;
  scans: Record<string, ScanRecord[]>;  // shopId → scans

  // Public shops (no auth)
  publicShops: PublicShop[];
  publicShopsLoading: boolean;

  // Public datasets (no auth, for browse page)
  publicDatasets: Dataset[];
  publicDatasetsLoading: boolean;

  fetchShops: () => Promise<void>;
  fetchPublicShops: (category?: string, q?: string) => Promise<void>;
  fetchPublicDatasets: () => Promise<void>;
  createShop: (name: string, address?: string, category?: string, location?: { lat: number; lng: number } | null) => Promise<Shop | null>;
  deleteShop: (shopId: string) => Promise<void>;
  addSection: (shopId: string, name: string) => Promise<void>;
  deleteSection: (shopId: string, sectionId: string) => Promise<void>;
  renameSection: (shopId: string, sectionId: string, name: string) => Promise<void>;
  updateSection: (shopId: string, sectionId: string, updates: { name?: string; posterFrameIndex?: number }) => Promise<void>;
  updateShop: (shopId: string, updates: Partial<Pick<Shop, 'name' | 'address' | 'visibility' | 'location' | 'description' | 'phone' | 'whatsapp' | 'openingHours' | 'posterSectionId'>>) => Promise<void>;
  fetchShopScans: (shopId: string) => Promise<void>;
  deleteScan: (shopId: string, scanId: string) => Promise<void>;
  retryScan: (shopId: string, scanId: string) => Promise<void>;
  initiateTransfer: (shopId: string) => Promise<{ token: string; expiresAt: string } | null>;
  cancelTransfer: (shopId: string) => Promise<void>;
}

export const useShopStore = create<ShopState>((set) => ({
  shops: [],
  loading: false,
  error: null,
  scans: {},
  publicShops: [],
  publicShopsLoading: false,
  publicDatasets: [],
  publicDatasetsLoading: false,

  fetchShops: async () => {
    set({ loading: true, error: null });
    try {
      const res = await fetch(apiUrl('/api/shops'), { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      set({ shops: data.shops, loading: false });
    } catch (e: any) {
      set({ error: e.message, loading: false });
    }
  },

  fetchPublicShops: async (category?: string, q?: string) => {
    set({ publicShopsLoading: true });
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (q) params.set('q', q);
      const qs = params.toString();
      const url = apiUrl('/api/shops/public') + (qs ? `?${qs}` : '');
      const res = await fetch(url);
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      set({ publicShops: data.shops, publicShopsLoading: false });
    } catch (e: any) {
      set({ error: e.message, publicShopsLoading: false });
    }
  },

  fetchPublicDatasets: async () => {
    set({ publicDatasetsLoading: true });
    try {
      const res = await fetch(apiUrl('/api/datasets'));
      if (!res.ok) throw new Error(`${res.status}`);
      const manifest: DatasetsManifest = await res.json();
      set({ publicDatasets: manifest.datasets, publicDatasetsLoading: false });
    } catch (e: any) {
      console.warn('[shopStore] Failed to load public datasets:', e.message);
      set({ publicDatasetsLoading: false });
    }
  },

  createShop: async (name, address = '', category = '', location = null) => {
    try {
      const res = await fetch(apiUrl('/api/shops'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ name, address, category, location }),
      });
      if (!res.ok) {
        if (res.status === 429) {
          const body = await res.json().catch(() => null);
          const msg = body?.detail || 'Shop limit reached';
          set({ error: msg });
          return null;
        }
        throw new Error(`${res.status}`);
      }
      const shop = await res.json();
      set((s) => ({ shops: [shop, ...s.shops] }));
      return shop;
    } catch (e: any) {
      set({ error: e.message });
      return null;
    }
  },

  deleteShop: async (shopId) => {
    try {
      await fetch(apiUrl(`/api/shops/${shopId}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      set((s) => ({ shops: s.shops.filter((sh) => sh.id !== shopId) }));
    } catch (e: any) {
      set({ error: e.message });
    }
  },

  addSection: async (shopId, name) => {
    try {
      const res = await fetch(apiUrl(`/api/shops/${shopId}/sections`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const updatedShop = await res.json();
      set((s) => ({
        shops: s.shops.map((sh) => (sh.id === shopId ? updatedShop : sh)),
      }));
    } catch (e: any) {
      set({ error: e.message });
    }
  },

  deleteSection: async (shopId, sectionId) => {
    try {
      const res = await fetch(apiUrl(`/api/shops/${shopId}/sections/${sectionId}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const updatedShop = await res.json();
      set((s) => ({
        shops: s.shops.map((sh) => (sh.id === shopId ? updatedShop : sh)),
      }));
    } catch (e: any) {
      set({ error: e.message });
    }
  },

  renameSection: async (shopId, sectionId, name) => {
    try {
      const res = await fetch(apiUrl(`/api/shops/${shopId}/sections/${sectionId}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const updatedShop = await res.json();
      set((s) => ({
        shops: s.shops.map((sh) => (sh.id === shopId ? updatedShop : sh)),
      }));
    } catch (e: any) {
      set({ error: e.message });
    }
  },

  updateSection: async (shopId, sectionId, updates) => {
    try {
      const res = await fetch(apiUrl(`/api/shops/${shopId}/sections/${sectionId}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const updatedShop = await res.json();
      set((s) => ({
        shops: s.shops.map((sh) => (sh.id === shopId ? updatedShop : sh)),
      }));
    } catch (e: any) {
      set({ error: e.message });
    }
  },

  updateShop: async (shopId, updates) => {
    try {
      const res = await fetch(apiUrl(`/api/shops/${shopId}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const updatedShop = await res.json();
      set((s) => ({
        shops: s.shops.map((sh) => (sh.id === shopId ? updatedShop : sh)),
      }));
    } catch (e: any) {
      set({ error: e.message });
    }
  },

  fetchShopScans: async (shopId) => {
    try {
      const res = await fetch(apiUrl(`/api/shops/${shopId}/scans`), { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      set((s) => ({ scans: { ...s.scans, [shopId]: data.scans } }));
    } catch (e: any) {
      set({ error: e.message });
    }
  },

  deleteScan: async (shopId, scanId) => {
    try {
      const res = await fetch(apiUrl(`/api/scans/${scanId}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      set((s) => ({
        scans: {
          ...s.scans,
          [shopId]: (s.scans[shopId] || []).filter((sc) => sc.id !== scanId),
        },
      }));
    } catch (e: any) {
      set({ error: e.message });
    }
  },

  retryScan: async (shopId, scanId) => {
    try {
      const res = await fetch(apiUrl(`/api/scans/${scanId}/retry`), {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      set((s) => ({
        scans: {
          ...s.scans,
          [shopId]: (s.scans[shopId] || []).map((sc) =>
            sc.id === scanId ? { ...sc, status: 'queued', completedAt: null } : sc
          ),
        },
      }));
    } catch (e: any) {
      set({ error: e.message });
    }
  },

  initiateTransfer: async (shopId) => {
    try {
      const res = await fetch(apiUrl(`/api/shops/${shopId}/transfer`), {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.detail || `${res.status}`);
      }
      const data = await res.json();
      // Update shop in local state with pendingTransfer
      set((s) => ({
        shops: s.shops.map((sh) =>
          sh.id === shopId
            ? { ...sh, pendingTransfer: { token: data.token, createdAt: new Date().toISOString(), expiresAt: data.expiresAt } }
            : sh
        ),
      }));
      return data;
    } catch (e: any) {
      set({ error: e.message });
      return null;
    }
  },

  cancelTransfer: async (shopId) => {
    try {
      const res = await fetch(apiUrl(`/api/shops/${shopId}/transfer`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      set((s) => ({
        shops: s.shops.map((sh) =>
          sh.id === shopId ? { ...sh, pendingTransfer: null } : sh
        ),
      }));
    } catch (e: any) {
      set({ error: e.message });
    }
  },
}));
